/**
 * Favorites Store
 * Manages favorite circuits with localStorage persistence
 */

import { create } from 'zustand';
import type { Circuit } from '@/types';

const STORAGE_KEY = 'anouk-favorite-circuits';

function loadFromStorage(): Circuit[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as Circuit[];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveToStorage(circuits: Circuit[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(circuits));
  } catch {
    // ignore
  }
}

interface FavoritesStore {
  favoriteCircuits: Circuit[];
  addFavorite: (circuit: Circuit) => void;
  removeFavorite: (id: number) => void;
  isFavorite: (id: number) => boolean;
  toggleFavorite: (circuit: Circuit) => void;
}

export const useFavoritesStore = create<FavoritesStore>((set, get) => ({
  favoriteCircuits: loadFromStorage(),

  addFavorite: (circuit) => {
    set((state) => {
      if (state.favoriteCircuits.some((c) => c.id === circuit.id)) return state;
      const next = [...state.favoriteCircuits, circuit];
      saveToStorage(next);
      return { favoriteCircuits: next };
    });
  },

  removeFavorite: (id) => {
    set((state) => {
      const next = state.favoriteCircuits.filter((c) => c.id !== id);
      saveToStorage(next);
      return { favoriteCircuits: next };
    });
  },

  isFavorite: (id) => get().favoriteCircuits.some((c) => c.id === id),

  toggleFavorite: (circuit) => {
    const { isFavorite, addFavorite, removeFavorite } = get();
    if (isFavorite(circuit.id)) {
      removeFavorite(circuit.id);
    } else {
      addFavorite(circuit);
    }
  },
}));
